import React, {
  useState,
  useRef,
  useEffect,
  useMemo,
  useCallback,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Drawer,
  Form,
  Select,
  Spin,
} from 'antd';
import PropTypes from 'prop-types';
import pdfMake from 'pdfmake';
import { useTranslation } from 'react-i18next';

import { getCompanyImageURL } from '../../settings/state/settings.actions';
import { getBasePdfDefinition, getBase64ImageFromURL } from '../../projects/ProjectScheduleOfValues/PDF/sov.pdf.helpers';
import DrawerSubmitFooter from '../../common/containers/DrawerSubmitFooter';
import GanttScheduleColumnsSection from './GanttScheduleColumnsSection';
import { escapeValueForCSV, getGanttColumns, getVisibleColumnMap } from './ganttScheduleHelpers';
import OnTraccrSwitch from '../../common/inputs/OnTraccrSwitch';
import { getIdMap } from '../../helpers/helpers';
import { formatProjectLabelFromCompanySettings } from '../../projects/projectHelpers';
import {
  buildGanttPdfImagePages,
  captureGanttScheduleScreenshot,
  COMPANY_IMAGE_HEADER_HEIGHT,
  GANTT_EXPORT_VIEW_TYPES,
  LANDSCAPE_CONTENT_HEIGHT,
  LANDSCAPE_PDF_CONTENT_WIDTH,
  waitForNextPaint,
} from './ganttScheduleExport.helpers';

const buildCompanyImageHeader = async (isCompanyImageOn, companyImageURL) => {
  const pdfHeader = {
    columns: [],
    style: 'pdfHeader',
  };

  if (!isCompanyImageOn || !companyImageURL) {
    return null;
  }

  try {
    const image = await getBase64ImageFromURL(companyImageURL);
    if (image) {
      pdfHeader.columns.push({
        image,
        width: 150,
        maxWidth: 150,
        maxHeight: 100,
      });
    }
  } catch (e) {
    // silently fail
  }

  return pdfHeader.columns.length ? pdfHeader : null;
};

export default function GanttScheduleExportDrawer(props) {
  const {
    tasks,
    onClose,
    visible,
    userSettings,
    isMaster,
    onEnsureScheduleView,
    visibleColumns,
    onVisibleColumnsChange,
    exportViewType = GANTT_EXPORT_VIEW_TYPES.GANTT,
    onExportViewTypeChange,
  } = props;
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const companyImageURL = useSelector((state) => state.settings.company.companyImageURL);
  const projects = useSelector((state) => state.projects.projects);
  const { settings = {} } = useSelector((state) => state.settings.company);

  const [exportFormat, setExportFormat] = useState('pdf');
  const [pdfDefinition, setPdfDefinition] = useState(null);
  const [isCompanyImageOn, setIsCompanyImageOn] = useState(!!companyImageURL);
  const [pdf, setPdf] = useState(null);
  const [csvData, setCSVData] = useState();
  const [isGeneratingPreview, setIsGeneratingPreview] = useState(false);
  const [previewError, setPreviewError] = useState(null);

  const pdfRef = useRef(null);

  const ganttColumns = useMemo(() => getGanttColumns(isMaster), [isMaster]);
  const projectMap = useMemo(() => getIdMap(projects), [projects]);
  const isListExport = exportViewType === GANTT_EXPORT_VIEW_TYPES.LIST;

  const projectFormatter = useCallback((value) => {
    const project = projectMap[value];
    if (!project) return '';
    return formatProjectLabelFromCompanySettings({
      name: project.name,
      number: project.number,
      settings,
    });
  }, [projectMap, settings]);

  useEffect(() => {
    dispatch(getCompanyImageURL());
  }, []);

  useEffect(() => {
    setIsCompanyImageOn(!!companyImageURL);
  }, [companyImageURL]);

  useEffect(() => {
    if (!visible) return;

    onExportViewTypeChange(GANTT_EXPORT_VIEW_TYPES.GANTT);
    setExportFormat('pdf');
    setPreviewError(null);
    onEnsureScheduleView?.();

    let initialVisibleColumns = [ganttColumns[0]];
    if (userSettings.ganttScheduleSettings) {
      try {
        initialVisibleColumns = JSON.parse(userSettings.ganttScheduleSettings).visibleColumns;
      } catch (e) {
        initialVisibleColumns = [ganttColumns[0]];
      }
    }
    onVisibleColumnsChange(initialVisibleColumns);
  }, [
    userSettings,
    visible,
    ganttColumns,
    onEnsureScheduleView,
    onVisibleColumnsChange,
    onExportViewTypeChange,
  ]);

  const onCompanyImageToggled = (e) => {
    setIsCompanyImageOn(e);
  };

  const onExportViewTypeChangeInner = useCallback((value) => {
    onExportViewTypeChange(value);
    setPreviewError(null);
    if (value === GANTT_EXPORT_VIEW_TYPES.GANTT) {
      setExportFormat('pdf');
      onEnsureScheduleView?.();
    }
  }, [onEnsureScheduleView, onExportViewTypeChange]);

  useEffect(() => {
    if (
      visible
      && exportFormat === 'pdf'
      && pdfDefinition
      && pdfRef.current
    ) {
      const pdfDocGenerator = pdfMake.createPdf(pdfDefinition);
      pdfDocGenerator.getDataUrl((dataUrl) => {
        pdfRef.current.src = dataUrl;
      });

      setPdf(pdfDocGenerator);
    }
  }, [
    exportFormat,
    visible,
    pdfDefinition,
    pdfRef,
  ]);

  useEffect(() => {
    if (!visible) return undefined;

    let isCancelled = false;

    const generateListExportData = async () => {
      let csv = '';
      const newPdfDefinition = {
        ...getBasePdfDefinition('portrait'),
        content: [],
      };

      const pdfHeader = await buildCompanyImageHeader(isCompanyImageOn, companyImageURL);
      if (pdfHeader) {
        newPdfDefinition.content.push(pdfHeader);
      }

      const formattedVisibleColumns = visibleColumns.map((column) => (
        column === 'Project' ? t('Project') : column
      ));
      const tableHeaders = formattedVisibleColumns.map((column) => ({
        text: column,
        style: 'tableHeader',
      }));

      csv += `${formattedVisibleColumns.join(',')}\r\n`;

      const visibleColumnMap = getVisibleColumnMap({
        t,
        tasks,
        activeTab: '',
        projectFormatter,
      });

      const table = {
        headerRows: 1,
        body: [],
      };

      const tableRows = [];
      tasks.forEach((task) => {
        const row = [];
        visibleColumns.forEach((column) => {
          const col = visibleColumnMap[column];
          row.push(
            col.pdfFormat
              ? col.pdfFormat(task[col.property])
              : {
                text: task[col.property],
                style: 'tableCell',
              },
          );
          csv += escapeValueForCSV(
            col.csvFormat
              ? col.csvFormat(task[col.property])
              : task[col.property],
          );
        });
        csv += '\r\n';
        tableRows.push(row);
      });

      table.body.push(tableHeaders, ...tableRows);
      newPdfDefinition.content.push({
        table,
      });

      if (!isCancelled) {
        setPdfDefinition(newPdfDefinition);
        setCSVData(csv);
      }
    };

    const generateGanttExportData = async () => {
      const newPdfDefinition = {
        ...getBasePdfDefinition('landscape'),
        content: [],
      };

      const pdfHeader = await buildCompanyImageHeader(isCompanyImageOn, companyImageURL);
      const hasCompanyHeader = !!pdfHeader;
      if (hasCompanyHeader) {
        newPdfDefinition.content.push(pdfHeader);
      }

      await waitForNextPaint();
      const image = await captureGanttScheduleScreenshot();
      const imagePages = await buildGanttPdfImagePages({
        imageDataUrl: image,
        contentWidth: LANDSCAPE_PDF_CONTENT_WIDTH,
        firstPageAvailableHeight: LANDSCAPE_CONTENT_HEIGHT - (
          hasCompanyHeader ? COMPANY_IMAGE_HEADER_HEIGHT : 0
        ),
        subsequentPageAvailableHeight: LANDSCAPE_CONTENT_HEIGHT,
      });
      newPdfDefinition.content.push(...imagePages);

      if (!isCancelled) {
        setPdfDefinition(newPdfDefinition);
        setCSVData(undefined);
      }
    };

    const generateExportData = async () => {
      setIsGeneratingPreview(true);
      setPreviewError(null);
      try {
        if (isListExport) {
          await generateListExportData();
        } else {
          await generateGanttExportData();
        }
      } catch (error) {
        if (!isCancelled) {
          setPreviewError(error?.message || 'Unable to generate export preview.');
          setPdfDefinition(null);
        }
      } finally {
        if (!isCancelled) {
          setIsGeneratingPreview(false);
        }
      }
    };

    if (exportFormat === 'pdf') {
      generateExportData();
    }

    return () => {
      isCancelled = true;
    };
  }, [
    tasks,
    visibleColumns,
    isCompanyImageOn,
    t,
    projectFormatter,
    visible,
    exportFormat,
    exportViewType,
    isListExport,
    companyImageURL,
  ]);

  const updateExportFormat = (e) => {
    setExportFormat(e);
  };

  const onExport = () => {
    if (exportFormat === 'pdf') {
      pdf.download();
    } else {
      const csv = csvData;
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.setAttribute('href', url);
      link.setAttribute('download', 'export.csv');
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  return (
    <Drawer
      title="Export"
      visible={visible}
      onClose={onClose}
      width={1100}
      destroyOnClose
    >
      <Form.Item label="Export View">
        <Select onChange={onExportViewTypeChangeInner} value={exportViewType}>
          <Select.Option value={GANTT_EXPORT_VIEW_TYPES.GANTT}>Gantt View</Select.Option>
          <Select.Option value={GANTT_EXPORT_VIEW_TYPES.LIST}>List View</Select.Option>
        </Select>
      </Form.Item>
      { isListExport && (
        <Form.Item label="Export Format">
          <Select onChange={updateExportFormat} value={exportFormat}>
            <Select.Option value="pdf">PDF</Select.Option>
            <Select.Option value="csv">csv</Select.Option>
          </Select>
        </Form.Item>
      )}
      <GanttScheduleColumnsSection
        visibleColumns={visibleColumns}
        setVisibleColumns={onVisibleColumnsChange}
      />
      { exportFormat === 'pdf' && (
        <>
          <Form.Item>
            <OnTraccrSwitch
              value={isCompanyImageOn}
              onChange={onCompanyImageToggled}
              title="Load in company image"
            />
          </Form.Item>
          {previewError && (
            <p style={{ color: '#cf1322', marginBottom: 16 }}>{previewError}</p>
          )}
          <Spin spinning={isGeneratingPreview}>
            <iframe
              title="Gantt PDF"
              ref={pdfRef}
              className="sov-pdf-preview-frame"
              style={{
                marginBottom: 25,
                height: '75vh',
                minHeight: 650,
              }}
            />
          </Spin>
        </>
      )}
      <DrawerSubmitFooter
        onClose={onClose}
        onSubmit={onExport}
        submitTitle="Export"
        loading={isGeneratingPreview}
        canSubmit={
          exportFormat !== 'pdf'
          || (!isGeneratingPreview && !previewError && !!pdfDefinition)
        }
      />
    </Drawer>
  );
}

GanttScheduleExportDrawer.propTypes = {
  visible: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onEnsureScheduleView: PropTypes.func,
  visibleColumns: PropTypes.arrayOf(PropTypes.string),
  onVisibleColumnsChange: PropTypes.func,
  exportViewType: PropTypes.string,
  onExportViewTypeChange: PropTypes.func,
  tasks: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    start: PropTypes.instanceOf(Date).isRequired,
    end: PropTypes.instanceOf(Date).isRequired,
    progress: PropTypes.number.isRequired,
    dependencies: PropTypes.arrayOf(PropTypes.string),
    custom_class: PropTypes.string,
  })).isRequired,
  userSettings: PropTypes.shape({
    ganttScheduleSettings: PropTypes.string,
  }).isRequired,
  isMaster: PropTypes.bool.isRequired,
};

GanttScheduleExportDrawer.defaultProps = {
  onEnsureScheduleView: undefined,
  visibleColumns: [],
  onVisibleColumnsChange: undefined,
  exportViewType: GANTT_EXPORT_VIEW_TYPES.GANTT,
  onExportViewTypeChange: () => {},
};
